import facebook from "@iconify/icons-mdi/facebook";
import pinterest from "@iconify/icons-mdi/pinterest";
import twitter from "@iconify/icons-mdi/twitter";
import instagram from "@iconify/icons-mdi/instagram";
import { Icon } from "@iconify/react";
import { Fade, IconButton, makeStyles } from "@material-ui/core";
import { Delete, Edit, FileCopy, Image, MoreVert, PictureAsPdf, Print } from "@material-ui/icons/";
import PropTypes, { func } from "prop-types";
import React, { Fragment, useState } from "react";
import { FoldersApi } from "../../../api/folders/folders";
import CustomConfirmationDialog from "../../../components/CustomConfirmationDialog/CustomConfirmationDialog";
import SectionedPopupMenu from "../../../components/Menus/PopupMenu/SectionedPopupMenu";
import { Permissions } from "../../../data/Permissions";
import { useSnackbar } from "../../../data/SnackbarContext";
import { DELETE_ITEM_DIALOG, ITEM_MENU_ACTIONS, SNACKBAR_MESSAGES } from "../../../data/StringConstants";
import { useToggle } from "../../../hooks/useToggle";
import analytics from "../../../utils/analytics";
import CopyItemDialogContainer from '../../Dialogs/CopyItemDialogContainer/CopyItemDialogContainer';
import DeleteItemConfirmationDialogContent from "./DeleteItemConfirmationDialogContent";
import { addInstagramIconToItemCardMenuAfterFacebookIcon, pushAddToWidgetMenuItem, pushSendEmailMenuItems, removeViewPdfMenuItem, updatePrintTargetForWebItem } from "./itemCardMenuUtilities";


const useStyles = makeStyles(theme => ({
	iconifyStyles: {
		width: "24px",
		height: "24px"
	},
	itemCardMenuStyles: {
		position: "absolute",
		zIndex: 1,
		marginLeft: "197px",
		marginTop: "5px",
		backgroundColor: theme.palette.background.default,
		border: `1px solid ${theme.palette.primary.main}`,
		"&:hover": {
			backgroundColor: theme.palette.primary.extraLight
		}
	}
}));

/**
 * An instance of the SectionedPopupMenu component used for a menu of actions that can be taken on an item.
 */
export default function ItemCardMenu(props) {
	const classes = useStyles();
	const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();

	const { item, show, deleteItem, copyItem } = props;
	const [deleteItemDialogIsOpen, setDeleteItemDialogIsOpen] = useState(false);

	const [showCopyDialog, toggleCopyDialog] = useToggle(false);
	const [disableDeleteButton, toggleDeleteButtonDisabled] = useToggle(false);

	const [anchorEl, setAnchorEl] = useState(null);

	const handleClick = event => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = (menuItemLabel) => {
		setAnchorEl(null);
		if (menuItemLabel) {
			analytics.logEvent(analytics.eventTypes.FOLDERS.ITEM_CARD_MENU_CLICKED, {
				item: {
					id: item.Id,
					name: item.Name
				},
				itemCardMenuItem: menuItemLabel
			});
		}
	};

	const openDeleteDialog = () => {
		setDeleteItemDialogIsOpen(true);
	};

	const closeDeleteDialog = () => {
		analytics.logEvent(analytics.eventTypes.FOLDERS.DELETE_ITEM_CONFIRMATION_CLICKED, {
				item: {
					id: item.Id,
					name: item.Name
				},
				cancelOrDelete: "cancel"
			});
		setDeleteItemDialogIsOpen(false);
	};

	var defaultItemMenuActions = {
		primaryActions: [
			{
				label: ITEM_MENU_ACTIONS.EDIT,
				icon: <Edit />,
				permissions: [Permissions.editor],
				target: item.EditUrl,
				noNewTab: true
			},
			{
				label: ITEM_MENU_ACTIONS.COPY,
				icon: <FileCopy />,
				permissions: [Permissions.editor],
				action: () => {
					toggleCopyDialog();
				},
			}
		],
		viewActions: [
			{
				label: ITEM_MENU_ACTIONS.VIEW_PDF,
				icon: <PictureAsPdf />,
				permissions: [Permissions.viewer],
				target: item.PdfDownloadUrl
			},
			{
				label: ITEM_MENU_ACTIONS.VIEW_IMG,
				icon: <Image />,
				permissions: [Permissions.viewer],
				target: item.ThumbnailDownloadUrl
			},
			{
				label: ITEM_MENU_ACTIONS.PRINT,
				icon: <Print />,
				permissions: [Permissions.viewer],
				target: item.PdfDownloadUrl
			}
		],
		shareActions:
		[
			{
				label: ITEM_MENU_ACTIONS.FACEBOOK,
				icon: (
					<Icon icon={facebook} className={classes.iconifyStyles} />
				),
				permissions: [Permissions.publisher],
				target: item.PostUrl
			},
			{
				label: ITEM_MENU_ACTIONS.PINTEREST,
				icon: (
					<Icon icon={pinterest} className={classes.iconifyStyles} />
				),
				permissions: [Permissions.publisher],
				target: item.ThumbnailDownloadUrl
			},
		],
		destructiveActions: [
			{
				destructive: true,
				icon: <Delete />,
				label: ITEM_MENU_ACTIONS.DELETE,
				action: () => {
					openDeleteDialog();
				},
				permissions: [Permissions.editor],
			}
		]
	};

	if (item.InstagramUrl) {
		var instagramShareAction = {
			label: ITEM_MENU_ACTIONS.INSTAGRAM,
			icon: (
				<Icon id={`publishInstagram${item.Id}`} icon={instagram} className={classes.iconifyStyles} />
			),
			permissions: [Permissions.publisher],
			target: item.InstagramUrl
		};
		defaultItemMenuActions.shareActions = addInstagramIconToItemCardMenuAfterFacebookIcon(defaultItemMenuActions.shareActions, instagramShareAction);
	}

	let itemMenuActions = { ...defaultItemMenuActions };

	if (item.FormatName && item.FormatName.toLowerCase().includes("widget")) {
		itemMenuActions = pushAddToWidgetMenuItem(itemMenuActions, item);
		itemMenuActions = updatePrintTargetForWebItem(itemMenuActions, item);
		itemMenuActions = removeViewPdfMenuItem(itemMenuActions);
	} else if (item.FormatName && item.FormatName.toLowerCase().includes("e-blast")) {
		itemMenuActions = pushSendEmailMenuItems(itemMenuActions, item);
		itemMenuActions = updatePrintTargetForWebItem(itemMenuActions, item);
		itemMenuActions = removeViewPdfMenuItem(itemMenuActions);
	}

	const handleDeleteItem = async () => {
		toggleDeleteButtonDisabled();
		try {
			analytics.logEvent(analytics.eventTypes.FOLDERS.DELETE_ITEM_CONFIRMATION_CLICKED, {
				item: {
					id: item.Id,
					name: item.Name
				},
				cancelOrDelete: "delete"
			});

			const response = await FoldersApi.deleteItem(item.Id);

			if (response.status === 200) {
				deleteItem();
				showSuccessSnackbar(SNACKBAR_MESSAGES.DELETE_ITEM_SUCCESS);
			}
		} catch (error) {
			showErrorSnackbar(SNACKBAR_MESSAGES.DELETE_ITEM_ERROR);
		} finally {
			toggleDeleteButtonDisabled();
		}
	};

	return (
		<Fragment>
			<SectionedPopupMenu
				menuItemSections={[
					itemMenuActions.primaryActions,
					itemMenuActions.viewActions,
					itemMenuActions.shareActions,
					itemMenuActions.destructiveActions
				]}
				triggerButton={() => (
					<div style={{ position: "relative", height: "auto" }}>
						<Fade in={show ? show : undefined}>
							<IconButton
								id={`itemMenu${item.Id}`}
								className={classes.itemCardMenuStyles}
								onClick={handleClick}
								color="primary"
							>
								<MoreVert />
							</IconButton>
						</Fade>
					</div>
				)}
				handleClose={handleClose}
				anchorEl={anchorEl}
			/>
			{ /* start: Dialogue Components ---- */}
			<CopyItemDialogContainer
				stateDelegate={copyItem}
				item={item}
				open={showCopyDialog}
				toggle={toggleCopyDialog}
			/>
			<CustomConfirmationDialog
				onClose={closeDeleteDialog}
				title={DELETE_ITEM_DIALOG.TITLE}
				open={deleteItemDialogIsOpen}
			>
				<DeleteItemConfirmationDialogContent
					onClickCancel={closeDeleteDialog}
					onClickConfirm={handleDeleteItem}
					disableDeleteButton={disableDeleteButton}
				/>
			</CustomConfirmationDialog>
			{ /* end: Dialogue Components ---- */}
		</Fragment>
	);
}

ItemCardMenu.propTypes = {
	item: PropTypes.shape({
		EditUrl: PropTypes.string,
		PdfDownloadUrl: PropTypes.string,
		PreviewUrl: PropTypes.string,
		PrintUrl: PropTypes.string,
		PostUrl: PropTypes.string,
		InstagramUrl: PropTypes.string,
		TweetUrl: PropTypes.string,
		ThumbnailDownloadUrl: PropTypes.string,
		PromotionContextId: PropTypes.string,
	}),
	copyItem: PropTypes.func,
	deleteItem: PropTypes.func
};
